/*
 * List continuation (spec 12 §4.4): Enter on a list line carries the marker to the next line, an
 * empty item ends the list and takes its marker with it, and Tab indents inside a list only — a
 * Tab anywhere else keeps moving focus, which a keyboard user relies on.
 */

import type { Edit } from "./edit.ts"

/** Indent, marker, content: `- `, `* `, `+ `, `1. `, `1) `, `- [ ] `, `- [x] `, `> `. */
const ITEM = /^(\s*)((?:[-*+] \[[ xX]\] )|(?:[-*+] )|(?:\d+[.)] )|(?:> ?))(.*)$/

export const INDENT = "  "

function lineStartOf(text: string, at: number): number {
	return text.lastIndexOf("\n", at - 1) + 1
}

function nextMarker(marker: string): string {
	const ordered = /^(\d+)([.)] )$/.exec(marker)
	if (ordered?.[1] !== undefined && ordered[2] !== undefined) {
		return `${Number(ordered[1]) + 1}${ordered[2]}`
	}
	// A finished task starts the next one unchecked.
	return marker.replace(/\[[xX]\]/, "[ ]")
}

/**
 * The edit Enter makes on a list line, or null when the line is not a list item and Enter should
 * do what it does anywhere.
 */
export function continueList(text: string, caret: number): Edit | null {
	const lineStart = lineStartOf(text, caret)
	const m = ITEM.exec(text.slice(lineStart, caret))
	if (m === null || m[1] === undefined || m[2] === undefined || m[3] === undefined) return null
	const [, indent, marker, content] = m
	if (content.trim() === "") {
		// An empty item ends the list: the marker goes, the caret stays on a plain line.
		const rest = text.slice(caret)
		return { text: text.slice(0, lineStart) + rest, start: lineStart, end: lineStart }
	}
	const insert = `\n${indent}${nextMarker(marker)}`
	const at = caret + insert.length
	return { text: text.slice(0, caret) + insert + text.slice(caret), start: at, end: at }
}

/**
 * Indents or outdents every list line the selection touches; null when none of them is a list
 * item, so the caller lets Tab move focus.
 */
export function indentList(
	text: string,
	start: number,
	end: number,
	outdent: boolean,
): Edit | null {
	const firstLine = lineStartOf(text, start)
	const lastLineEnd = (() => {
		const nl = text.indexOf("\n", end)
		return nl === -1 ? text.length : nl
	})()
	const lines = text.slice(firstLine, lastLineEnd).split("\n")
	if (!lines.some((line) => ITEM.test(line))) return null

	let firstDelta = 0
	let totalDelta = 0
	const changed = lines.map((line, index) => {
		if (!ITEM.test(line)) return line
		let next: string
		if (outdent) {
			const removable = Math.min(INDENT.length, line.length - line.trimStart().length)
			next = line.slice(removable)
		} else {
			next = INDENT + line
		}
		const delta = next.length - line.length
		if (index === 0) firstDelta = delta
		totalDelta += delta
		return next
	})
	const result = text.slice(0, firstLine) + changed.join("\n") + text.slice(lastLineEnd)
	return {
		text: result,
		start: Math.max(firstLine, start + firstDelta),
		end: Math.max(firstLine, end + totalDelta),
	}
}
